import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useTranslation } from "react-i18next";
import { getSettingsMenuGroups, getSettingsMenuItems } from "./settingsMenuItems";
import "./scrollbar-hide.css";

const SettingsSideBar = ({ activePage }) => {
    const history = useHistory();
    const { t } = useTranslation("settings");
    const isMountedRef = useRef(true);
    const timeoutRef = useRef(null);

    const items = useMemo(() => getSettingsMenuItems(t), [t]);
    const groups = useMemo(
        () =>
            getSettingsMenuGroups(t)
                .map((group) => ({ ...group, items: items.filter((item) => item.group === group.key) }))
                .filter((group) => group.items.length > 0),
        [items, t],
    );

    useEffect(
        () => () => {
            isMountedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    const handleTabClick = useCallback(
        (value) => {
            if (activePage === value) {
                return;
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    history.push(`/settings?menu=${value}`);
                }
            }, 0);
        },
        [activePage, history],
    );

    return (
        <aside
            className="flex max-h-[calc(100vh-6.75rem)] w-[232px] shrink-0 flex-col rounded-2xl bg-white p-3 shadow-[0_1px_3px_rgba(16,16,40,0.06)] ring-1 ring-[#ECECF3] xl:w-[252px]"
            data-tour="settings-sidebar">
            <h2 className="px-3 pb-4 pt-2 text-[18px] font-semibold text-[#2F2F46]">{t("settings")}</h2>

            <nav
                className="scrollbar-hide flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto"
                aria-label={t("settings")}>
                {groups.map((group) => (
                    <div key={group.key} className="flex flex-col gap-0.5">
                        <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#9B9BA5]">
                            {group.label}
                        </p>
                        {group.items.map((item) => {
                            const isActive = activePage === item.value;

                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    data-tour={item.tourId}
                                    aria-current={isActive ? "page" : undefined}
                                    onClick={() => handleTabClick(item.value)}
                                    className={`relative flex w-full items-center gap-3 rounded-xl px-3 py-[9px] text-left text-[14px] transition-colors ${
                                        isActive
                                            ? "bg-[#EEEEFE] font-semibold text-[#5D5DF6]"
                                            : "font-medium text-[#4B4B5C] hover:bg-[#F5F5FA] hover:text-[#2F2F46]"
                                    }`}>
                                    {isActive ? (
                                        <span
                                            className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-[#5D5DF6]"
                                            aria-hidden
                                        />
                                    ) : null}
                                    <span
                                        className={`flex shrink-0 items-center [&_svg]:h-[18px] [&_svg]:w-[18px] ${isActive ? "text-[#5D5DF6]" : "text-[#9B9BA5]"}`}>
                                        {item.icon}
                                    </span>
                                    <span className="truncate">{item.label}</span>
                                </button>
                            );
                        })}
                    </div>
                ))}
            </nav>
        </aside>
    );
};

SettingsSideBar.propTypes = {
    activePage: PropTypes.string,
};

export default SettingsSideBar;
